import { useState } from 'react'
import { Trash2 } from 'lucide-react'
import { Button, Modal, ViewToggle } from '@/components/ui'
import { useOdooLineQty } from '../api/queries'
import type { OdooLineQty, ShipConfirmationTracker } from '../api/types'

export interface SplitLineModalProps {
  tracker: ShipConfirmationTracker | null
  isPending: boolean
  onCancel: () => void
  onSubmit: (splits: { quantity: string; latest_estimated_date?: string }[]) => void
}

interface Row {
  quantity: string
  latestEstimatedDate: string
}

type EntryUnit = 'package' | 'uom'

const EMPTY_ROW: Row = { quantity: '', latestEstimatedDate: '' }

/** Rounds to the backend's 4-decimal Numeric(18, 4) precision so a
 *  packages→UoM conversion doesn't leak float noise into the sum check. */
function round4(n: number): number {
  return Math.round(n * 10000) / 10000
}

function fmt(n: number): string {
  return n.toLocaleString('en-US', { maximumFractionDigits: 4 })
}

/** "Tote (800kg)" → "tote". Odoo packaging names carry the size in
 *  parentheses; the size is shown separately, from the line's own numbers. */
function packageNoun(packagingName: string): string {
  return packagingName.replace(/\s*\(.*\)\s*$/, '').trim().toLowerCase() || 'package'
}

function plural(noun: string, n: number): string {
  return n === 1 ? noun : `${noun}s`
}

interface Units {
  uom: string
  /** UoM per package (e.g. 800 kg per tote), null when the line has no packaging. */
  packageSize: number | null
  packageNoun: string | null
}

function unitsFor(lineQty: OdooLineQty): Units {
  const ordered = Number(lineQty.product_qty)
  const count = lineQty.packaging_qty ? Number(lineQty.packaging_qty) : 0
  const hasPackaging = !!lineQty.packaging_name && count > 0 && ordered > 0
  return {
    uom: lineQty.uom_name ?? 'units',
    packageSize: hasPackaging ? ordered / count : null,
    packageNoun: hasPackaging ? packageNoun(lineQty.packaging_name!) : null,
  }
}

/**
 * "Split this line" — breaks one PO-line tracker into partial shipments,
 * each with its own quantity + (optional) estimated ship date, so a vendor
 * shipping a line in parts (e.g. 10 of 28 totes now, the rest undated) can
 * be confirmed one shipment at a time.
 *
 * The operator enters only the shipments they know about; the final
 * "remaining" split is computed as ordered − entered, so the breakdown
 * always sums to the ordered quantity and never needs hand-balancing.
 * When the Odoo line has packaging (e.g. "Tote (800kg)"), quantities are
 * entered in packages by default and converted to the line's UoM on submit
 * — the backend only ever sees UoM quantities.
 *
 * The client-side arithmetic is UX-speed only — it runs against
 * `useOdooLineQty`'s snapshot. The real guard is the backend's
 * live-Odoo-read validation in `create_splits`
 * (app/services/po_shipment_confirmation_splits.py), which re-reads
 * `product_qty` at submit time.
 */
export function SplitLineModal({ tracker, isPending, onCancel, onSubmit }: SplitLineModalProps) {
  const [rows, setRows] = useState<Row[]>([EMPTY_ROW])
  const [remainderDate, setRemainderDate] = useState('')
  const [unit, setUnit] = useState<EntryUnit>('package')
  const { data: lineQty, isLoading: qtyLoading } = useOdooLineQty(tracker?.id ?? null)

  if (!tracker) return null

  const orderedQty = lineQty ? Number(lineQty.product_qty) : null
  const units = lineQty ? unitsFor(lineQty) : null
  const entryUnit: EntryUnit = units?.packageSize ? unit : 'uom'
  const factor = entryUnit === 'package' && units?.packageSize ? units.packageSize : 1

  /** Entered value → line UoM. */
  const toUom = (value: string) => round4((Number(value) || 0) * factor)
  const describe = (uomQty: number) => {
    if (!units) return fmt(uomQty)
    const inUom = `${fmt(uomQty)} ${units.uom}`
    if (!units.packageSize || !units.packageNoun) return inUom
    const pkgs = round4(uomQty / units.packageSize)
    return `${fmt(pkgs)} ${plural(units.packageNoun, pkgs)} (${inUom})`
  }

  const enteredTotal = round4(rows.reduce((sum, r) => sum + toUom(r.quantity), 0))
  const remainder = orderedQty != null ? round4(orderedQty - enteredTotal) : null
  const rowsValid = rows.every((r) => Number(r.quantity) > 0)
  const overEntered = remainder != null && remainder <= 0 && enteredTotal > 0
  const canSubmit = !qtyLoading && orderedQty != null && rowsValid && remainder != null && remainder > 0
  const splitCount = rows.length + 1

  const unitLabel =
    entryUnit === 'package' && units?.packageNoun
      ? `${units.packageNoun}s`
      : (units?.uom ?? '')

  function reset() {
    setRows([EMPTY_ROW])
    setRemainderDate('')
    setUnit('package')
  }

  function changeUnit(next: EntryUnit) {
    if (next === entryUnit || !units?.packageSize) return
    // Carry what's been typed across so switching units doesn't lose it.
    const size = units.packageSize
    setRows((prev) =>
      prev.map((r) => {
        if (r.quantity === '') return r
        const n = Number(r.quantity)
        return { ...r, quantity: String(round4(next === 'uom' ? n * size : n / size)) }
      }),
    )
    setUnit(next)
  }

  function updateRow(index: number, patch: Partial<Row>) {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)))
  }

  function addRow() {
    setRows((prev) => [...prev, { ...EMPTY_ROW }])
  }

  function removeRow(index: number) {
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)))
  }

  function submit() {
    if (remainder == null) return
    onSubmit([
      ...rows.map((r) => ({
        quantity: String(toUom(r.quantity)),
        latest_estimated_date: r.latestEstimatedDate || undefined,
      })),
      { quantity: String(remainder), latest_estimated_date: remainderDate || undefined },
    ])
  }

  const inputClass =
    'rounded-xs border border-border-default bg-surface px-2 py-1 text-sm text-primary focus:border-border-focus focus:outline-none'

  return (
    <Modal
      open={tracker !== null}
      onOpenChange={(next) => {
        if (!next) {
          reset()
          onCancel()
        }
      }}
      tone="info"
      title="Split this line into partial shipments"
      description={
        <>
          Break <strong>{tracker.odoo_po_name}</strong>
          {tracker.product_name ? <> — {tracker.product_name}</> : null} into separate
          shipments. Enter what's shipping; whatever is left becomes the last split, with or
          without a date.
        </>
      }
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => {
              reset()
              onCancel()
            }}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button variant="primary" loading={isPending} disabled={!canSubmit} onClick={submit}>
            Create {splitCount} splits
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {qtyLoading ? (
          <div className="text-sm text-tertiary">Checking ordered quantity…</div>
        ) : orderedQty != null ? (
          <div
            className={`rounded-xs px-2 py-1.5 text-sm ${
              overEntered ? 'bg-danger-subtle text-danger' : 'bg-surface-sunk text-secondary'
            }`}
          >
            {overEntered
              ? `Entered ${describe(enteredTotal)} — that's the whole order of ${describe(orderedQty)} or more. Leave some for the remaining split.`
              : `Ordered: ${describe(orderedQty)}.`}
          </div>
        ) : null}

        {units?.packageSize && units.packageNoun ? (
          <div className="flex items-center gap-2 text-sm text-secondary">
            <span>Enter quantities in</span>
            <ViewToggle<EntryUnit>
              aria-label="Quantity unit"
              value={entryUnit}
              onChange={changeUnit}
              options={[
                {
                  value: 'package',
                  label: `${units.packageNoun}s (${fmt(units.packageSize)} ${units.uom} each)`,
                },
                { value: 'uom', label: units.uom },
              ]}
            />
          </div>
        ) : null}

        {rows.map((row, i) => (
          <div key={i} className="flex items-start gap-2">
            <label className="flex flex-1 flex-col gap-1 text-sm">
              <span className="text-secondary">
                Split {i + 1} quantity{unitLabel ? ` (${unitLabel})` : ''}
              </span>
              <input
                type="number"
                min="0"
                step="any"
                value={row.quantity}
                onChange={(e) => updateRow(i, { quantity: e.target.value })}
                className={inputClass}
              />
              {entryUnit === 'package' && Number(row.quantity) > 0 && units ? (
                <span className="text-role-metadata">
                  = {fmt(toUom(row.quantity))} {units.uom}
                </span>
              ) : null}
            </label>
            <label className="flex flex-1 flex-col gap-1 text-sm">
              <span className="text-secondary">Estimated ship date (optional)</span>
              <input
                type="date"
                value={row.latestEstimatedDate}
                onChange={(e) => updateRow(i, { latestEstimatedDate: e.target.value })}
                className={inputClass}
              />
            </label>
            <Button
              variant="ghost"
              size="sm"
              disabled={rows.length <= 1}
              onClick={() => removeRow(i)}
              aria-label={`Remove split ${i + 1}`}
              className="mt-6"
            >
              <Trash2 size={14} />
            </Button>
          </div>
        ))}

        <Button variant="ghost" size="sm" onClick={addRow} className="self-start">
          + Add another split
        </Button>

        <div className="flex items-start gap-2 border-t border-border-subtle pt-3">
          <div className="flex flex-1 flex-col gap-1 text-sm">
            <span className="text-secondary">Split {splitCount} — remaining</span>
            <div
              data-testid="split-remainder"
              className="rounded-xs border border-border-subtle bg-surface-sunk px-2 py-1 text-sm text-primary"
            >
              {remainder != null && remainder > 0 ? describe(remainder) : '—'}
            </div>
          </div>
          <label className="flex flex-1 flex-col gap-1 text-sm">
            <span className="text-secondary">Estimated ship date (leave blank if unknown)</span>
            <input
              type="date"
              aria-label="Remaining split estimated ship date"
              value={remainderDate}
              onChange={(e) => setRemainderDate(e.target.value)}
              className={inputClass}
            />
          </label>
          {/* Keeps the remainder row's columns aligned with the trash-button rows above. */}
          <div className="w-8 shrink-0" aria-hidden />
        </div>
      </div>
    </Modal>
  )
}
